import React from 'react';
import { Volume2, ArrowRight, Play } from 'lucide-react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';

// Components
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// Assets
import heroBg from '../assets/aigc/images/hero-sound-waves_1771110044_000.png';
import musicArt from '../assets/aigc/images/music-library-art_1771110044_000.png';
import sessionArt from '../assets/aigc/images/sound-bath-session_1771110044_000.png';

const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 200]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  return (
    <section className="relative h-screen flex items-center justify-center overflow-hidden">
      {/* Parallax Background */}
      <motion.div style={{ y }} className="absolute inset-0 z-0">
        <img src={heroBg} alt="Sound Waves" className="w-full h-full object-cover scale-110" />
        <div className="absolute inset-0 bg-background/40 mix-blend-multiply"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/20 to-background"></div>
      </motion.div>

      <motion.div 
        style={{ opacity }}
        className="relative z-10 text-center px-6 max-w-5xl mx-auto"
      >
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          <h2 className="text-primary uppercase tracking-[0.4em] text-xs md:text-sm mb-6 font-sans font-medium">Sound & Consciousness</h2>
        </motion.div>
        
        <motion.h1 
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2, ease: "easeOut" }}
          className="text-6xl md:text-8xl lg:text-9xl font-serif text-white mb-8 leading-none"
        >
          Resonance <br/> <span className="italic text-secondary font-light">& Presence</span>
        </motion.h1>
        
        <motion.p 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4, ease: "easeOut" }}
          className="text-text-muted text-lg md:text-xl max-w-2xl mx-auto mb-12 font-light leading-relaxed font-sans"
        >
          Immersive musical compositions and sound-bath experiences designed to support inner balance, emotional release, and conscious presence.
        </motion.p>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.6, ease: "easeOut" }}
          className="flex flex-col md:flex-row gap-6 justify-center items-center"
        >
          <Link to="/music" className="group relative px-8 py-4 bg-transparent border border-primary/30 text-primary hover:text-background hover:bg-primary rounded-full transition-all duration-500 overflow-hidden">
            <span className="relative z-10 flex items-center gap-3 font-sans text-sm tracking-widest uppercase">
              <Play size={14} className="fill-current" /> Explore Music
            </span>
          </Link>
        </motion.div>
      </motion.div>
      
      <motion.div 
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-12 left-1/2 transform -translate-x-1/2 text-white/30"
      >
        <div className="w-[1px] h-16 bg-gradient-to-b from-transparent via-white/30 to-transparent mx-auto mb-2"></div>
        <span className="text-[10px] uppercase tracking-widest">Scroll</span>
      </motion.div>
    </section>
  );
};

const About = () => {
  return (
    <section id="about" className="py-32 bg-background relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/5 to-transparent"></div>
      
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-12 gap-16 items-center">
          
          {/* Left Column: Abstract/Quote */}
          <div className="md:col-span-5 relative">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="relative z-10"
            >
              <Volume2 className="w-16 h-16 text-primary/50 mb-8" strokeWidth={1} />
              <h2 className="text-4xl md:text-5xl font-serif text-white leading-tight mb-8">
                "Sound is not only something we hear, but something we <span className="italic text-secondary">physically</span> and <span className="italic text-secondary">energetically</span> experience."
              </h2>
              <div className="w-24 h-1 bg-primary/30"></div>
            </motion.div>
            
            {/* Background decoration */}
            <div className="absolute -top-20 -left-20 w-96 h-96 bg-secondary/5 rounded-full blur-3xl"></div>
          </div>

          {/* Right Column: Content */}
          <div className="md:col-span-7 space-y-8 text-text-muted font-light text-lg leading-loose">
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Kapi Levente is a sound artist and facilitator whose work centers on sound-bath experiences and deeply immersive musical compositions. Through carefully composed frequencies, layered textures, and intuitive musical expression, his sessions create a space where the nervous system can slow down.
            </motion.p>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Each sound journey is designed as a lived experience rather than a performance — guided personally, held intentionally, and shaped in real time. Music is at the core of his work, blending spirituality with musical craftsmanship.
            </motion.p>
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="pt-4"
            >
              <a href="#contact" className="text-primary hover:text-white transition-colors border-b border-primary/30 hover:border-white pb-1 text-sm uppercase tracking-widest">
                Read Full Bio
              </a>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Offerings = () => {
  return (
    <section className="py-32 bg-surface relative">
      <div className="container mx-auto px-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-24"
        >
          <span className="text-primary text-xs font-sans uppercase tracking-[0.3em] block mb-4">Offerings</span>
          <h2 className="text-4xl md:text-5xl font-serif text-white">Ways to Experience</h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 max-w-7xl mx-auto">
          
          {/* Music Library Card */}
          <Link to="/music">
            <motion.div 
              id="music"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="group relative h-[600px] overflow-hidden rounded-sm cursor-pointer"
            >
              <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors duration-700 z-10"></div>
              <img src={musicArt} alt="Music Library" className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
              
              <div className="absolute inset-0 z-20 flex flex-col justify-end p-12">
                <div className="transform transition-transform duration-500 group-hover:-translate-y-4">
                  <div className="flex items-center gap-4 mb-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">
                    <span className="w-12 h-[1px] bg-primary"></span>
                    <span className="text-primary text-xs uppercase tracking-widest">Digital Collection</span>
                  </div>
                  <h3 className="text-4xl font-serif text-white mb-4">Music Library</h3>
                  <p className="text-slate-300 font-light leading-relaxed mb-8 max-w-md opacity-80 group-hover:opacity-100 transition-opacity">
                    Original sound journeys created for meditation, relaxation, and emotional processing. Available for download.
                  </p>
                  <div className="flex items-center text-white group-hover:text-primary transition-colors">
                    <span className="text-sm uppercase tracking-widest mr-4">Listen Now</span>
                    <ArrowRight size={16} />
                  </div>
                </div>
              </div>
            </motion.div>
          </Link>

          {/* Programs & Sessions Card */}
          <Link to="/programs">
            <motion.div 
              id="programs"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="group relative h-[600px] overflow-hidden rounded-sm cursor-pointer"
            >
              <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors duration-700 z-10"></div>
              <img src={sessionArt} alt="Sound Bath Sessions" className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
              
              <div className="absolute inset-0 z-20 flex flex-col justify-end p-12">
                <div className="transform transition-transform duration-500 group-hover:-translate-y-4">
                  <div className="flex items-center gap-4 mb-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">
                    <span className="w-12 h-[1px] bg-secondary"></span>
                    <span className="text-secondary text-xs uppercase tracking-widest">In Person</span>
                  </div>
                  <h3 className="text-4xl font-serif text-white mb-4">Programs & Sessions</h3>
                  <p className="text-slate-300 font-light leading-relaxed mb-8 max-w-md opacity-80 group-hover:opacity-100 transition-opacity">
                    Personally guided sound-bath experiences. A direct environment to experience the transformative effect of sound.
                  </p>
                  <div className="flex items-center text-white group-hover:text-secondary transition-colors">
                    <span className="text-sm uppercase tracking-widest mr-4">View Schedule</span>
                    <ArrowRight size={16} />
                  </div>
                </div>
              </div>
            </motion.div>
          </Link>

        </div>
      </div>
    </section>
  );
};

const LandingPage = () => {
  return (
    <div className="bg-background min-h-screen text-text font-sans selection:bg-primary selection:text-background overflow-x-hidden">
      {/* Noise Overlay */}
      <div className="fixed inset-0 pointer-events-none z-50 opacity-[0.03] mix-blend-overlay" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }}></div>
      
      <Navbar />
      <Hero />
      <About />
      <Offerings />
      <Footer />
    </div>
  );
};

export default LandingPage;
